import React, { useState, useEffect } from 'react';
import { Modal, Steps, Select, Button, Typography, Space, notification, Spin, Card, Divider } from 'antd';
import { CopyOutlined, FolderOutlined, FileTextOutlined, CheckOutlined } from '@ant-design/icons';
import { useSettingsState } from '../../state/settingsState';
import { copyChecklist, getChecklists } from '../../api/checklists';

const { Title, Text } = Typography;
const { Option } = Select;

const ChecklistCopyModal = () => {
  const {
    copyModalVisible,
    copyingChecklist,
    pipelines,
    statuses,
    selectedTargetPipeline,
    selectedTargetStatus,
    copyInProgress,
    closeCopyModal,
    setSelectedTargetPipeline,
    setSelectedTargetStatus,
    setCopyInProgress,
    setChecklists,
    selectedStatus
  } = useSettingsState();

  const [currentStep, setCurrentStep] = useState(0);
  const [copyResult, setCopyResult] = useState(null);

  useEffect(() => {
    if (copyModalVisible) {
      setCurrentStep(0);
      setCopyResult(null);
    } else {
      
      setCurrentStep(0);
      setCopyResult(null);
      setSelectedTargetPipeline(null);
      setSelectedTargetStatus(null);
    }
  }, [copyModalVisible, setSelectedTargetPipeline, setSelectedTargetStatus]);

  const steps = [
    {
      title: 'Выбор воронки',
      icon: <FolderOutlined />,
      description: 'Выберите целевую воронку'
    },
    {
      title: 'Выбор статуса',
      icon: <FileTextOutlined />,
      description: 'Выберите целевой статус'
    },
    {
      title: 'Подтверждение',
      icon: <CheckOutlined />,
      description: 'Подтвердите копирование'
    }
  ];

  const handlePipelineSelect = (pipelineId) => {
    const pipeline = Object.values(pipelines).find(p => p && p.id === parseInt(pipelineId));
    if (pipeline) {
      setSelectedTargetPipeline(pipeline);
    }
  };

  const handleStatusSelect = (statusId) => {
    const status = statuses.find(s => s && s.amo_status_id === parseInt(statusId));
    if (status) {
      setSelectedTargetStatus(status);
    }
  };

  const isNextDisabled = () => {
    switch (currentStep) {
      case 0:
        const availablePipelines = Object.values(pipelines).filter(pipeline => 
          pipeline && pipeline.id && pipeline.name
        );
        return !selectedTargetPipeline || availablePipelines.length === 0;
      case 1:
        const filteredStatuses = statuses.filter(s => 
          selectedTargetPipeline && s && s.pipeline_id === selectedTargetPipeline.id && s.amo_status_id && s.name
        );
        return !selectedTargetStatus || filteredStatuses.length === 0;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCopy = async () => {
    if (!copyingChecklist || !selectedTargetStatus || !selectedStatus) {
      notification.error({
        message: 'Ошибка валидации',
        description: 'Не все данные для копирования выбраны',
        placement: 'bottomRight'
      });
      return;
    }

    setCopyInProgress(true);
    
    try {
      const result = await copyChecklist(
        selectedStatus.amo_status_id,
        copyingChecklist.id,
        selectedTargetStatus.amo_status_id
      );

      if (result.success) {
        setCopyResult(result.data);
        
        const successMessage = `Чеклист "${copyingChecklist.name}" успешно скопирован`;
        const detailsMessage = [
          `Скопировано кастомных полей: ${result.data.copied_custom_fields_count || 0}`,
          `Скопировано дочерних чеклистов: ${result.data.copied_children_count || 0}`,
          `Новое название: "${result.data.copied_checklist?.name || 'Не указано'}"`
        ].join('\n');
        
        notification.success({
          message: successMessage,
          description: detailsMessage,
          placement: 'bottomRight',
          duration: 5
        });

        
        if (selectedTargetStatus.amo_status_id === selectedStatus.amo_status_id) {
          try {
            const updatedChecklists = await getChecklists(selectedStatus.amo_status_id);
            setChecklists(updatedChecklists);
          } catch (updateError) {
            console.warn('Не удалось обновить список чеклистов:', updateError);
          }
        }

        setTimeout(() => {
          closeCopyModal();
        }, 2000);
      } else {
        throw new Error(result.error || 'Неизвестная ошибка при копировании');
      }
    } catch (error) {
      console.error('Ошибка копирования чеклиста:', error);
      
      const errorMessage = error.message || 'Не удалось скопировать чеклист';
      const errorDescription = error.status ? 
        `Код ошибки: ${error.status}. ${errorMessage}` : 
        errorMessage;
      
      notification.error({
        message: 'Ошибка копирования',
        description: errorDescription,
        placement: 'bottomRight',
        duration: 0 
      });
    } finally {
      setCopyInProgress(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        const availablePipelines = Object.values(pipelines).filter(pipeline => 
          pipeline && pipeline.id && pipeline.name
        );
        
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Title level={4}>Выберите целевую воронку</Title>
            <Text type="secondary">
              Выберите воронку, в которую хотите скопировать чеклист "{copyingChecklist?.name}"
            </Text>
            {availablePipelines.length === 0 ? (
              <Text type="secondary" style={{ textAlign: 'center', padding: '20px' }}>
                Нет доступных воронок для копирования
              </Text>
            ) : (
              <Select
                placeholder="Выберите воронку"
                style={{ width: '100%' }}
                value={selectedTargetPipeline?.id}
                onChange={handlePipelineSelect}
                size="large"
                listHeight={200}
                dropdownStyle={{ maxHeight: '200px' }}
              >
                {availablePipelines.map(pipeline => (
                  <Option key={pipeline.id} value={pipeline.id}>
                    <Space>
                      <FolderOutlined />
                      {pipeline.name}
                    </Space>
                  </Option>
                ))}
              </Select>
            )}
          </Space>
        );
      
      case 1:
        const filteredStatuses = statuses.filter(s => 
          selectedTargetPipeline && s && s.pipeline_id === selectedTargetPipeline.id && s.amo_status_id && s.name
        );
        
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Title level={4}>Выберите целевой статус</Title>
            <Text type="secondary">
              Воронка: <strong>{selectedTargetPipeline?.name}</strong>
            </Text>
            <Text type="secondary">
              Выберите статус в воронке "{selectedTargetPipeline?.name}"
            </Text>
            {filteredStatuses.length === 0 ? (
              <Text type="secondary" style={{ textAlign: 'center', padding: '20px' }}>
                В выбранной воронке нет доступных статусов
              </Text>
            ) : (
              <Select
                placeholder="Выберите статус"
                style={{ width: '100%' }}
                value={selectedTargetStatus?.amo_status_id}
                onChange={handleStatusSelect}
                size="large"
                listHeight={180}
                dropdownStyle={{ maxHeight: '180px' }}
              >
                {filteredStatuses.map(status => (
                  <Option key={status.amo_status_id} value={status.amo_status_id}>
                    <Space>
                      <FileTextOutlined />
                      {status.name}
                    </Space>
                  </Option>
                ))}
              </Select>
            )}
          </Space>
        );
      
      case 2:
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Title level={4}>Подтверждение копирования</Title>
            <Card style={{ backgroundColor: '#f9f9f9' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text><strong>Исходный чеклист:</strong> {copyingChecklist?.name}</Text>
                <Text><strong>Целевая воронка:</strong> {selectedTargetPipeline?.name}</Text>
                <Text><strong>Целевой статус:</strong> {selectedTargetStatus?.name}</Text>
                <Divider />
                <Text type="secondary">
                  Будут скопированы все кастомные поля, настройки отображения и дочерние чеклисты.
                </Text>
                {selectedTargetStatus?.amo_status_id === selectedStatus?.amo_status_id && (
                  <Text type="warning">
                    ⚠️ Копирование происходит в тот же статус - будет создан дубликат
                  </Text>
                )}
              </Space>
            </Card>
            
            {copyResult && (
              <Card style={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}>
                <Space direction="vertical">
                  <Text type="success"><CheckOutlined /> Копирование завершено успешно!</Text>
                  <Text>Скопировано кастомных полей: <strong>{copyResult.copied_custom_fields_count}</strong></Text>
                  <Text>Скопировано дочерних чеклистов: <strong>{copyResult.copied_children_count}</strong></Text>
                  <Text>Название скопированного чеклиста: <strong>{copyResult.copied_checklist?.name}</strong></Text>
                </Space>
              </Card>
            )}
          </Space>
        );
      
      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (copyResult) {
      return [
        <Button key="close" onClick={closeCopyModal}>
          Закрыть
        </Button>
      ];
    }

    return [
      <Button key="cancel" onClick={closeCopyModal} disabled={copyInProgress}>
        Отмена
      </Button>,
      currentStep > 0 && (
        <Button key="back" onClick={handlePrev} disabled={copyInProgress}>
          Назад
        </Button>
      ),
      currentStep < steps.length - 1 ? (
        <Button 
          key="next" 
          type="primary" 
          onClick={handleNext}
          disabled={isNextDisabled() || copyInProgress}
        >
          Далее
        </Button>
      ) : (
        <Button 
          key="copy" 
          type="primary" 
          icon={<CopyOutlined />}
          onClick={handleCopy}
          loading={copyInProgress}
          disabled={copyInProgress || !copyingChecklist || !selectedTargetStatus}
        >
          Копировать
        </Button>
      )
    ].filter(Boolean);
  };

  return (
    <Modal
      title={
        <Space>
          <CopyOutlined />
          Копирование чеклиста
        </Space>
      }
      open={copyModalVisible}
      onCancel={closeCopyModal}
      footer={renderFooter()}
      width={600}
      destroyOnClose
      maskClosable={!copyInProgress}
      closable={!copyInProgress}
    >
      <Spin spinning={copyInProgress} tip="Копирование чеклиста...">
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Steps 
            current={currentStep} 
            items={steps}
            size="small"
          />
          
          <div style={{ minHeight: '200px', padding: '20px 0' }}>
            {renderStepContent()}
          </div>
        </Space>
      </Spin>
    </Modal>
  );
};

export default ChecklistCopyModal; 